/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ChangeDetectionStrategy,
	OnChanges,
	OnDestroy,
	Input,
	SimpleChanges,
	ChangeDetectorRef,
} from '@angular/core';
import { Subject, BehaviorSubject, merge, Observable, combineLatest } from 'rxjs';
import { map, takeUntil, debounceTime } from 'rxjs/operators';
import { PlanState } from 'src/app/services/plan-state.service';
import { Ticket } from '@touchplan/common';
import * as strings from '@touchplan/strings';
import { TicketEditService } from '../../ticket-edit/ticket-edit.service';
import { cssVar } from '@touchplan/css-var';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService, ILoggerService } from '@touchplan/logging';
import { TicketEditCommonDesktopService } from 'src/app/common/docked-ticket/services/ticket-edit-common-desktop.service';

@Component({
	selector: 'cycle-tickets',
	templateUrl: './cycle-tickets.component.html',
	styleUrls: ['./cycle-tickets.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('CycleTicketsComponent')],
})
export class CycleTicketsComponent implements OnChanges, OnDestroy {
	@Input() public tickets: Ticket[];
	@Input() public syncIsFloat: boolean;

	public AppStrings = strings;
	public sortedTickets: Ticket[];
	public ticket: Ticket;
	public currentTicketIndex: number;
	public numTickets: number;
	public fullMenu = true;
	public disableCycleButtons$: Observable<boolean>;

	private shutdownSubject$ = new Subject<void>();
	private disableCycleTicket$: BehaviorSubject<boolean>;
	private cycleTimer: ReturnType<typeof setTimeout>;
	private readonly CYCLE_LOCK_DELAY: number = 750;

	constructor(
		private changeDetectorRef: ChangeDetectorRef,
		private planState: PlanState,
		private ticketEditService: TicketEditService,
		private ticketEditCommonService: TicketEditCommonDesktopService,
		private logger: DefaultLoggerService
	) {
		this.disableCycleTicket$ = new BehaviorSubject(false);
		this.currentTicketIndex = 0;
		this.numTickets = 0;

		// handle tapped ticket
		this.planState.cycleTicket$.pipe(takeUntil(this.shutdownSubject$)).subscribe((ticket: Ticket) => {
			for (let i = 0; i < this.sortedTickets.length; i++) {
				if (ticket.$id === this.sortedTickets[i].$id) {
					this.currentTicketIndex = i;
					this.ticket = this.sortedTickets[i];
					break;
				}
			}
		});

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7105
		// cycle tickets mode
		// attempting to describe the current behavior:
		// - this observable will emit every time etc changes
		// - etc currently _many_ times with a empty list while closing the ticket edit
		// - when the list is empty it will emit a null
		// - cycling is implemented by closing one edit instance and opening another one
		// - so, by closing you'll get something like... true, many falses, then true once it's opened
		// - then the behavior of this component is configured to rely on that specific timing
		// in a particularly silly way conditionally executing things based on the value of `showFullMenu`
		// ---
		// - to replicate this in the new service we need effectively an observable that emits true
		// when edit is opened and false when it is closed in some way that's not impacted by the race timing
		// or angular optimizations
		// - ...I don't think it's sane to try and replicate this behavior
		// OLD VERSION
		ticketEditService.ticketTemplate$
			.pipe(
				debounceTime(100),
				map((data) => data === null),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((showFullMenu: boolean) => {
				console.log('CycleTicketsComponent: showFullMenu = ' + showFullMenu);
				this.fullMenu = showFullMenu;
				this.changeDetectorRef.detectChanges();
			});
		// NEW VERSION
		// ticketEditCommonService.uiInitialized$.pipe(
		// 	takeUntil(this.shutdownSubject$)
		// ).subscribe((showFullMenu: boolean) => {
		// 	this.fullMenu = showFullMenu;
		// 	// this.changeDetectorRef.detectChanges();
		// });
		// END PLACEHOLDER REPLACE AS PART OF TP-7105

		this.disableCycleButtons$ = combineLatest([
			this.disableCycleTicket$,
			ticketEditService.isEditValid$.pipe(map((valid) => !valid)), // make them both have "disable == true" sense
		]).pipe(
			map((both) => {
				const result = both[0] || both[1];
				// this.logger.log('both[0] = ' + both[0] + ', both[1] = ' + both[1] + ', result = ' + result);
				return result;
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	public ngOnChanges(changes: SimpleChanges) {
		const newTickets = changes['tickets'].currentValue;
		if (newTickets) {
			this.numTickets = newTickets.length;
			this.populateAndSortTickets();
		}
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		if (this.cycleTimer) {
			clearTimeout(this.cycleTimer);
		}
	}

	public previousTicket(): void {
		if (!this.ticketEditService.isEditValidSync) {
			return; // TP-1585
		}
		// TP-460: Temp fix
		if (this.cycleTimer) {
			return;
		}
		this.startTimer();
		// TP-460: Temp fix
		this.currentTicketIndex--;

		if (this.currentTicketIndex === -1) {
			this.currentTicketIndex = this.numTickets - 1;
		}
		this.selectAndHighlightTicket();
	}

	public nextTicket(): void {
		if (!this.ticketEditService.isEditValidSync) {
			return; // TP-1585
		}
		// TP-460: Temp fix
		if (this.cycleTimer) {
			return;
		}
		this.startTimer();
		// TP-460: Temp fix
		this.currentTicketIndex++;

		if (this.currentTicketIndex === this.numTickets) {
			this.currentTicketIndex = 0;
		}
		this.selectAndHighlightTicket();
	}

	public getTicketColor(): string {
		return this.ticket && this.ticket.view.liveRole ? this.ticket.view.liveRole.view.color : cssVar.black;
	}

	public openEdit(): void {
		this.planState.actions.changeTicketViewToEdit(
			this.ticket,
			this.planState.floatingTickets.editTicket$.getValue().size
				? this.planState.floatingTickets
				: this.planState.tickets
		);
	}

	private selectAndHighlightTicket(): void {
		this.planState.actions.unselectCycleTicket(
			this.ticket,
			this.syncIsFloat ? this.planState.floatingTickets : null
		);
		this.ticket = this.sortedTickets[this.currentTicketIndex];
		this.planState.actions.selectCycleTicket(
			this.ticket,
			this.syncIsFloat ? this.planState.floatingTickets : null
		);

		if (!this.fullMenu) {
			const selectedTicketList = this.planState.floatingTickets.editTicket$.getValue().size
				? this.planState.floatingTickets
				: this.planState.tickets;
			const editTickets = selectedTicketList.editTicket$.getValue();

			this.ticketEditCommonService.trySave();
			this.ticketEditService?.externalSaveCustomFieldsRequest$.next();
			this.planState.actions.changeTicketViewToDefault(editTickets, selectedTicketList);
			this.planState.actions.changeTicketViewToEdit(this.ticket, selectedTicketList);
		}
	}

	private populateAndSortTickets(): void {
		if (this.numTickets < 2) {
			return;
		}

		this.currentTicketIndex = 0;
		// sort tickets top -> bottom & left -> right & activityName alphabetical order
		this.sortedTickets = Array.from(this.tickets.values()).sort(
			(a, b) =>
				a.view.top - b.view.top ||
				a.view.left - b.view.left ||
				this.sortAlphabetically(a.view.activityName, b.view.activityName)
		);

		if (this.ticket && this.sortedTickets.filter((ticket) => ticket.$id === this.ticket.$id).length > 0) {
			this.planState.actions.unselectCycleTicket(
				this.ticket,
				this.syncIsFloat ? this.planState.floatingTickets : null
			);
		}

		this.ticket = this.sortedTickets[this.currentTicketIndex];
		this.planState.actions.selectCycleTicket(
			this.ticket,
			this.syncIsFloat ? this.planState.floatingTickets : null
		);
	}

	private sortAlphabetically(a: string, b: string): number {
		if (a < b) {
			return -1;
		}
		if (a > b) {
			return 1;
		}
		return 0;
	}

	// TP-460: Temp fix
	private startTimer(): void {
		this.disableCycleTicket$.next(true);
		this.cycleTimer = setTimeout(() => {
			this.cycleTimer = null;
			this.disableCycleTicket$.next(false);
		}, this.CYCLE_LOCK_DELAY);
	}
}
